





































































/* General */
import Vue from 'vue'
import { debounce } from 'lodash'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import AddGuestForm from '@/screens/guest/components/add-guest-form.vue'

/* Helpers */

/* Data types */
import { EEntities } from '@/types'
import {
  IPersonBase,
  IProjectsOutputAssignPeopleCompany,
} from '@/api/types/response.types'

/* API */
import InternalProjectAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default Vue.extend({
  name: 'GuestMembershipManager',
  components: {
    EntitySettingsLoading,
    AddGuestForm,
  },
  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    hideSuccessMessages: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      EEntities,
      isLoading: true,
      isSubmitting: false,
      debounceUpdateMemberships: null,
      sentInviteAccountMembershipIDs: [] as number[],
      companies: [] as IProjectsOutputAssignPeopleCompany[],
      memberIDs: [] as number[],
    }
  },
  computed: {
    isMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    assignedPeople(): IPersonBase[] {
      const result: IPersonBase[] = []
      this.companies.forEach(c => {
        c.listPeople.filter(p => p.isAssigned).forEach(p => {
          result.push({
            id: p.id,
            name: p.name,
            photoUrl: p.photoUrl,
          })
        })
      })
      return result.sort((a, b) => (a.name > b.name ? 1 : -1))
    },
  },

  watch: {
    assignedPeople: {
      immediate: false,
      handler(val) {
        this.$emit('memberships-updated', val)
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.getAssignPeopleInfo()
    this.debounceUpdateMemberships = debounce(this.updateMemberships, 500)
    this.isLoading = false
  },

  methods: {
    isMember(id: number): boolean {
      return this.memberIDs.includes(id)
    },
    hasEmployees(company: IProjectsOutputAssignPeopleCompany): boolean {
      return company.listPeople.length > 0
    },
    hasMultipleEmployees(company: IProjectsOutputAssignPeopleCompany): boolean {
      return company.listPeople.length > 1
    },
    allEmployeesAreMembers(
      company: IProjectsOutputAssignPeopleCompany,
    ): boolean {
      return company.listPeople.filter(p => !p.isAssigned).length === 0
    },
    inviteSent(accountMembershipID: number): boolean {
      return this.sentInviteAccountMembershipIDs.includes(accountMembershipID)
    },
    isCurrentUser(id: number): boolean {
      return this.$permissions.isLoggedInUser(id)
    },
    async getAssignPeopleInfo() {
      try {
        const response = await InternalProjectAPI.get_AssignGuests(
          this.entityId,
        )

        // Some implementers need the name of the entity (instead of doing its own separate call)...
        this.$emit('entity-name-fetched', response.name)
        this.companies = response.listCompanies
        this.memberIDs = this.assignedPeople.map(p => p.id)
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async sendSignInEmail(accountMembershipID: number) {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          accountMembershipID,
        )
        this.sentInviteAccountMembershipIDs.push(accountMembershipID)
        if (!this.hideSuccessMessages) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async addMembership(id: number) {
      if (!this.memberIDs.includes(id)) {
        this.memberIDs.push(id)
        await this.debounceUpdateMemberships()
      }
    },
    async removeMembership(id: number) {
      // Should never happen since we disable this on the UI
      if (this.isCurrentUser(id)) {
        return
      }
      this.memberIDs = this.memberIDs.filter(m => m !== id)
      this.debounceUpdateMemberships()
    },
    async addCompany(company: IProjectsOutputAssignPeopleCompany) {
      company.listPeople.map(m => m.id).forEach(id => {
        if (!this.memberIDs.includes(id)) {
          this.memberIDs.push(id)
        }
      })
      // Don't debounce, just call immediately...
      await this.updateMemberships()
    },
    async removeCompany(company: IProjectsOutputAssignPeopleCompany) {
      company.listPeople.map(m => m.id).forEach(id => {
        if (this.memberIDs.includes(id) && !this.isCurrentUser(id)) {
          this.memberIDs = this.memberIDs.filter(m => m !== id)
        }
      })
      // Don't debounce, just call immediately...
      await this.updateMemberships()
    },
    async updateMemberships() {
      try {
        this.isSubmitting = true
        const response = this.isMailbox
          ? await MailboxesAPI.post_AssignPeople(this.entityId, this.memberIDs)
          : await InternalProjectAPI.post_AssignPeople(
              this.entityId,
              this.memberIDs,
            )
        if (!this.hideSuccessMessages) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        await this.getAssignPeopleInfo()
      }
    },
  },
})
